import type { RefObject } from 'react';
import IssueTree from './IssueTree';
import GanttChart from './GanttChart';
import { useTimelineScroll } from './useTimelineScroll';
import type { FlatNode, TimeUnit } from './timelineUtils';
import type { ProjectIssueType } from '../../../hooks/useProjectIssueTypes';

interface DropTarget {
  issueId: string;
  valid: boolean;
}

interface Props {
  flattenedNodes: FlatNode[];
  timeUnit: TimeUnit;
  expandedIds: Set<string>;
  onToggle: (issueId: string) => void;
  onIssueClick: (issueId: string) => void;
  ganttScrollRef: RefObject<HTMLDivElement | null>;
  onDateChange: (issueId: string, field: 'startDate' | 'dueDate', newDate: string) => void;
  onMoveStart: (issueId: string, issueType: string, e: React.MouseEvent) => void;
  dropTarget: DropTarget | null;
  onCreateIssue?: (parentIssueId: string | null, issueType: string, title: string) => Promise<void>;
  projectIssueTypes?: ProjectIssueType[];
}

export default function TimelineBody({
  flattenedNodes,
  timeUnit,
  expandedIds,
  onToggle,
  onIssueClick,
  ganttScrollRef,
  onDateChange,
  onMoveStart,
  dropTarget,
  onCreateIssue,
  projectIssueTypes,
}: Props) {
  const { treeScrollRef, handleTreeScroll, handleGanttScroll } =
    useTimelineScroll(ganttScrollRef);

  return (
    <div className="flex flex-1 overflow-hidden">
      <IssueTree
        scrollRef={treeScrollRef}
        flattenedNodes={flattenedNodes}
        expandedIds={expandedIds}
        timeUnit={timeUnit}
        onToggle={onToggle}
        onIssueClick={onIssueClick}
        onScroll={handleTreeScroll}
        onCreateIssue={onCreateIssue}
        projectIssueTypes={projectIssueTypes}
      />
      <GanttChart
        scrollRef={ganttScrollRef}
        flattenedNodes={flattenedNodes}
        timeUnit={timeUnit}
        onIssueClick={onIssueClick}
        onScroll={handleGanttScroll}
        onDateChange={onDateChange}
        onMoveStart={onMoveStart}
        dropTarget={dropTarget}
      />
    </div>
  );
}
